import React, { ReactNode, RefCallback } from "react";
import {
  Controller,
  type Control,
  type ControllerRenderProps,
  type FieldError,
  type RegisterOptions,
} from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import CustomErrorMessage from "@/components/forms/inputsWithRef/customErrorMessage";

export interface CrudFieldRenderProps {
  field: ControllerRenderProps<any, string>;
  error?: FieldError;
}

export interface CrudFieldControllerProps {
  name: string;
  control: Control<any>;
  rules?: RegisterOptions;
  defaultValue?: unknown;
  validationTargetRef?: RefCallback<HTMLElement>;
  className?: string;
  groupInputWithError?: boolean;
  render: (renderProps: CrudFieldRenderProps) => ReactNode;
}

// Controller plus the error message every crud field shows below its input
const CrudFieldController = ({
  name,
  control,
  rules,
  defaultValue,
  validationTargetRef,
  className,
  groupInputWithError,
  render,
}: CrudFieldControllerProps) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    defaultValue={defaultValue}
    render={({ field, fieldState: { error }, formState: { errors } }) => {
      const input = render({ field, error });
      if (input === null) return <></>; // a field that hides itself hides its error message too
      const content = (
        <>
          {input}
          <ErrorMessage
            errors={errors}
            name={name}
            render={({ message }) => (
              <CustomErrorMessage error={"error"} errorMessage={message} />
            )}
          />
        </>
      );
      return validationTargetRef || className || groupInputWithError ? (
        <div ref={validationTargetRef} className={className}>
          {content}
        </div>
      ) : (
        content
      );
    }}
  />
);

export default CrudFieldController;
